import { SvgIcon, TextField } from '@mui/material'
import { IconButton } from '../icon-button/IconButton'
import { WithBreakpoints } from '@obeta/models/lib/models/Components'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './CreateEntityButton.module.scss'
import clsx from 'clsx'
import { DarkButton, TertiaryButton } from '../custom-button/CustomButton'
import { AddIcon, ChevronLeftIcon } from '../icons'
import { FormHelperText } from '../form-helper-text/FormHelperText'
import { Controller, useForm } from 'react-hook-form'
import { getCartTemplateCreateErrorText } from '@obeta/utils/lib/cart-templates'

export interface ICreateEntityButtontProps extends WithBreakpoints<'mobile'> {
  addEntityError?: string
  addEntityMode: boolean
  errorText?: string
  nameMaxLength: number
  placeholder: string
  title: string
  onCancel: () => void
  onEntityCreated: (name: string) => void
  setAddEntityMode: (addEntityMode: boolean) => void
}

export const CreateEntityButton: React.FC<ICreateEntityButtontProps> = (props) => {
  const {
    addEntityError,
    addEntityMode,
    nameMaxLength,
    placeholder,
    mobile,
    title,
    onCancel,
    onEntityCreated,
    setAddEntityMode,
  } = props

  const { t } = useTranslation()

  const {
    control,
    formState: { isValid, errors },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      name: '',
    },
    mode: 'onChange',
  })

  const errorText = t(
    getCartTemplateCreateErrorText(
      errors?.name?.type as 'maxLength' | 'required' | 'taken' | undefined
    ) ?? ''
  )

  useEffect(() => {
    addEntityError && setError('name', { type: addEntityError })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEntityError])

  const onNameSubmit = (values: { name: string }) => {
    onEntityCreated(values.name)
  }

  if (!addEntityMode) {
    return (
      <TertiaryButton
        size="large"
        fullWidth={false}
        onClick={() => setAddEntityMode(true)}
        leftIcon={<SvgIcon component={AddIcon} />}
      >
        {title}
      </TertiaryButton>
    )
  }

  const textField = (
    <div className={styles.content}>
      <Controller
        control={control}
        rules={{
          required: true,
          maxLength: nameMaxLength,
        }}
        name="name"
        render={({ field: { value, onChange } }) => (
          <>
            <TextField
              autoFocus
              className={clsx('outlined-lightGray', styles.textField)}
              InputProps={{
                classes: {
                  notchedOutline: styles.notchedOutline,
                },
              }}
              placeholder={placeholder}
              value={value}
              variant="outlined"
              onChange={onChange}
            />
            {errorText && errorText.length > 0 && <FormHelperText errorText={errorText} />}
          </>
        )}
      />
    </div>
  )

  if (mobile) {
    return (
      <div className={styles.textFieldWrapMobile}>
        {textField}
        <IconButton
          color="primary"
          disabled={!isValid}
          icon={<SvgIcon className={styles.applyIconMobile} component={ChevronLeftIcon} />}
          variant="contained"
          onClick={handleSubmit(onNameSubmit)}
        />
      </div>
    )
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(onNameSubmit)}>
      <div className={styles.textFieldWrapDesktop}>
        {textField}
        <div className={styles.actionsDesktop}>
          <TertiaryButton onClick={onCancel}>{t<string>('ALERTS.BTN_ABORT')}</TertiaryButton>
          <DarkButton disabled={!isValid} type="submit">
            {t<string>('BUTTON.SAVE')}
          </DarkButton>
        </div>
      </div>
    </form>
  )
}
